import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { TonePill } from '@client/components/_ui/display/TonePill';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { getRoleDisplayName } from '@client/utils/roles/role-config';
import { AUTH0_CONNECTIONS } from '@shared/constants';
import { Avatar, Dropdown, type MenuProps } from 'antd';
import { Ban, ChevronDown, KeyRound, Mail, Phone, Send, Settings2, ShieldOff } from 'lucide-react';
import type { UserData } from './types';

/** Best display name for a user: full name, then either name part, then email. */
export function getUserDisplayName(user: {
    firstName: string | null | undefined;
    lastName: string | null | undefined;
    email: string;
}) {
    if (user.firstName && user.lastName) {
        return `${user.firstName} ${user.lastName}`;
    }
    return user.firstName || user.lastName || user.email;
}

function MetaColumn({ label, children, wide = false }: { label: string; children: React.ReactNode; wide?: boolean }) {
    return (
        <div
            className={`flex flex-col gap-0.5 border-separator border-r pr-[18px] last:border-r-0 last:pr-0 ${
                // `wide` columns (e.g. a long role list) keep a sensible min/max width and wrap to a
                // new line instead of stretching the whole header horizontally.
                wide ? 'min-w-[140px] max-w-[400px]' : 'min-w-0'
            }`}
        >
            <span className="whitespace-nowrap font-medium text-2xs text-muted-foreground uppercase tracking-wide">
                {label}
            </span>
            <span className={`font-medium text-foreground text-sm ${wide ? 'break-words' : 'truncate'}`}>
                {children}
            </span>
        </div>
    );
}

/** Full-page header for the user detail: identity, status, key facts and account actions. */
export function UserDetailHeader({
    user,
    lastLoginLabel,
    onSendInvite,
    onResetMfa,
    onResetPassword,
    onRevokeAccess,
    backLink,
    readOnly = false,
}: {
    user: UserData;
    lastLoginLabel: string;
    onSendInvite: () => void;
    onResetMfa: () => void;
    onResetPassword: () => void;
    onRevokeAccess: () => void;
    /** A `BackLink` to the users list this detail page was opened from (e.g. "Back to team"). */
    backLink: React.ReactNode;
    /** Hide account actions when the viewer has read-only access. */
    readOnly?: boolean;
}) {
    const { formatPhoneNumber } = useFormatter();
    const displayName = getUserDisplayName(user);
    const initials = ((user.firstName?.[0] ?? '') + (user.lastName?.[0] ?? '') || user.email[0]).toUpperCase();
    const isDatabaseProvider = user.provider === AUTH0_CONNECTIONS.DATABASE.name;
    const rolesLabel = user.roles.length ? user.roles.map((role) => getRoleDisplayName(role)).join(', ') : '—';

    const actionItems: MenuProps['items'] = [
        ...(!user.provider ? [{ key: 'invite', icon: <Send className="size-4" />, label: 'Send invite' }] : []),
        ...(isDatabaseProvider
            ? [
                  {
                      key: 'reset-password',
                      icon: <KeyRound className="size-4" />,
                      label: 'Send reset password email',
                  },
                  { key: 'reset-mfa', icon: <ShieldOff className="size-4" />, label: 'Reset MFA' },
              ]
            : []),
        { type: 'divider' as const },
        {
            key: 'revoke',
            icon: <Ban className="size-4" />,
            label: 'Revoke access',
            danger: true,
            disabled: user.disabled,
        },
    ];

    const onMenuClick: MenuProps['onClick'] = ({ key }) => {
        if (key === 'invite') onSendInvite();
        if (key === 'reset-password') onResetPassword();
        if (key === 'reset-mfa') onResetMfa();
        if (key === 'revoke') onRevokeAccess();
    };

    return (
        <div>
            <div className="mb-2.5">{backLink}</div>
            <div className="flex items-start justify-between gap-4 rounded-lg border border-separator bg-card p-5">
                <div className="flex min-w-0 gap-4">
                    <Avatar src={user.photoUrl || undefined} size={56} className="shrink-0">
                        {initials}
                    </Avatar>
                    <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2.5">
                            <span className="font-bold text-foreground text-xl leading-tight">{displayName}</span>
                            {user.disabled ? (
                                <TonePill tone="danger">Disabled</TonePill>
                            ) : (
                                <TonePill tone="ok">Active</TonePill>
                            )}
                            {!user.provider ? <TonePill tone="warning">Invite pending</TonePill> : null}
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-muted-foreground text-sm">
                            <span className="flex min-w-0 items-center gap-1.5">
                                <Mail className="size-3.5 shrink-0" />
                                <span className="truncate">{user.email}</span>
                            </span>
                            {user.phone ? (
                                <span className="flex items-center gap-1.5">
                                    <Phone className="size-3.5 shrink-0" />
                                    {formatPhoneNumber(user.phone)}
                                </span>
                            ) : null}
                        </div>
                        <div className="mt-3.5 flex flex-wrap gap-x-[18px] gap-y-2">
                            <MetaColumn label="Position">{user.position || '—'}</MetaColumn>
                            <MetaColumn label="Roles" wide>
                                {rolesLabel}
                            </MetaColumn>
                            <MetaColumn label="Last login">{lastLoginLabel}</MetaColumn>
                        </div>
                    </div>
                </div>

                {!readOnly ? (
                    <Dropdown
                        menu={{ items: actionItems, onClick: onMenuClick }}
                        trigger={['click']}
                        placement="bottomRight"
                    >
                        <Button type="secondary" icon={<Settings2 className="size-4" />}>
                            Account actions
                            <ChevronDown className="size-4" />
                        </Button>
                    </Dropdown>
                ) : null}
            </div>
        </div>
    );
}
